import {
  simulate,
  DEFAULT_MAX_SIMULATION_TIME,
  type ControlInterval,
  type SimulationOptions,
  type SimulationResult,
} from '@rcs/physics';
import type { SampledTrack } from '@rcs/track-geometry';

/** 操作した直後に、メインスレッドで先に計算しておく時間 [s]（すぐに車両の動きへ反映するため）。 */
export const QUICK_HORIZON = 3;

/** 途中から計算し直した走行。 */
export interface ContinuedRide {
  readonly result: SimulationResult;
  /** 先の一部だけを計算した仮の結果か（全体の結果は Worker で計算する） */
  readonly provisional: boolean;
}

/** 時刻 `time` 以前で、最も遅いフレームの番号（二分探索）。 */
export function frameIndexAtOrBefore(result: SimulationResult, time: number): number {
  const frames = result.frames;
  let low = 0;
  let high = frames.length - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if ((frames[middle]?.time ?? Infinity) <= time) low = middle;
    else high = middle - 1;
  }
  return low;
}

/**
 * これまでの走行 `base` の、時刻 `time` 以前の最後のフレームから、操作の入力 `control` を加えて続きを計算し、
 * つなげた結果を返す。物理は決定的なので、つないだ結果は、0 から通して計算した結果と一致する
 * （`horizon` を指定したときは、その時間だけ先まで計算した仮の結果）。
 *
 * @param options 走行の条件（区間・抵抗など。`base` を計算したときと同じもの）
 * @param horizon 先へ計算する時間 [s]（省略で最後まで）
 */
export function continueRide(
  track: SampledTrack,
  options: SimulationOptions,
  base: SimulationResult,
  control: readonly ControlInterval[],
  time: number,
  horizon?: number,
): ContinuedRide {
  const index = frameIndexAtOrBefore(base, time);
  const from = base.frames[index];
  if (from === undefined) throw new RangeError('走行の結果にフレームがありません');
  const maxTime = options.maxTime ?? DEFAULT_MAX_SIMULATION_TIME;
  const limit = horizon === undefined ? maxTime : Math.min(maxTime, from.time + horizon);
  const tail = simulate(track, {
    ...options,
    control,
    startTime: from.time,
    startS: from.s,
    startSpeed: from.speed,
    maxTime: limit,
  });
  const provisional = tail.meta.termination === 'maxTime' && limit < maxTime;
  return {
    provisional,
    result: {
      frames: [...base.frames.slice(0, index), ...tail.frames],
      events: [...base.events.filter((event) => event.time <= from.time), ...tail.events],
      meta: tail.meta,
    },
  };
}
